import { LogicalOperator } from './table-filter'

export enum ActionStatus {
  New = 'new',
  Reappearance = 'reappearance',
  Ignored = 'ignored',
  Requested = 'requested',
  InProgress = 'inprogress',
  Accepted = 'accepted',
  Refused = 'refused',
  Reminded = 'reminded',
  Blocked = 'blocked',
  Disappeared = 'disappeared',
}

export const ACTION_STATUS_COLORS: Record<ActionStatus, string> = {
  [ActionStatus.Requested]: '#0a009b',
  [ActionStatus.InProgress]: '#faba22',
  [ActionStatus.Reminded]: '#ff8000',
  [ActionStatus.Ignored]: '#960b0b',
  [ActionStatus.Disappeared]: '#035607',
  [ActionStatus.Refused]: '#e51400',
  [ActionStatus.Accepted]: '#129519',
  [ActionStatus.New]: '#65bcbb',
  [ActionStatus.Reappearance]: '#7c1add',
  [ActionStatus.Blocked]: '#737a81',
} as const

enum BrandType {
  Official = 'official',
  Competitor = 'competitor',
  Industry = 'industry',
}

type ActionEngine = 'GOOGLE' | 'BING'

export interface ActionLinksDTO {
  cancel?: string
  markAsRequested?: string
  markAsIgnored?: string
  markAsAccepted?: string
  markAsRefused?: string
  markAsInprogress?: string
  cancelIgnore?: string
  markAsBlocked?: string
  cancelRequested?: string
  forceReminderCycle?: string
}

export interface ActionTag {
  ID: string
  label: string
}

export interface ActionHistoryDTO {
  ID: string
  status: ActionStatus
  actionID: string
  date: string
}

export interface ActionDTO {
  ID: string
  domain: string
  status: ActionStatus
  createdDate: string
  lastView: string
  views: number
  lastStatusDate?: string
  brandID?: string
  updatedDate?: string
  engine?: ActionEngine
  country?: string
  waiting?: boolean
  remindedCount?: number
  totalRemindedCount?: number
  receivedCount?: number
  sendedCount?: number
  unreadCount?: number
  requestedDate?: string
  reappearanceDate?: string
  ignoredDate?: string
  refusedDate?: string
  acceptedDate?: string
  inprogressDate?: string
  lastReceivedDate?: string
  remindedDate?: string
  blockedDate?: string
  disappearedDate?: string
  advertiserID?: string
  links?: ActionLinksDTO
  tags?: ActionTag[]
  history?: ActionHistoryDTO[]
}

interface ActionValueFilterRow {
  property: string
  operator?: string
  values?: string[]
}

interface ActionGroupFilterRow {
  property: 'group'
  logicalOperator: LogicalOperator
  rows: ActionFilterRow[]
}

export type ActionFilterRow = ActionValueFilterRow | ActionGroupFilterRow

export interface ActionFiltersDTO {
  logicalOperator: LogicalOperator
  rows: ActionFilterRow[]
}

interface ActionListRequestDTO {
  brandID: string
  engine: ActionEngine
  country: string
  filters: ActionFiltersDTO
  waiting?: boolean
  remindedCount?: number
  totalRemindedCount?: number
  receivedCount?: number
  sendedCount?: number
  unreadCount?: number
  requestedDate?: string
  reappearanceDate?: string
  ignoredDate?: string
  refusedDate?: string
  acceptedDate?: string
  inprogressDate?: string
  lastReceivedDate?: string
  remindedDate?: string
  blockedDate?: string
  disappearedDate?: string
  advertiserID?: string
}

interface ActionListPaginationLinks {
  next?: string
  prev?: string
  exportCSV: string
}

export interface ActionListResponseDTO {
  data: ActionDTO[]
  meta: { page: { total?: number }; filters: ActionListRequestDTO }
  links: ActionListPaginationLinks
}

export interface ActionForAdvertiserDTO {
  ID: string
  domain: string
  status: ActionStatus
  lastStatusDate: string
  createdDate: string
  lastView: string
  brandID: string
  updatedDate: string
  engine: ActionEngine
  country: string
  waiting: boolean
  remindedCount: number
  totalRemindedCount: number
  receivedCount: number
  sendedCount: number
  unreadCount: number
  requestedDate: string
  reappearanceDate: string
  ignoredDate: string
  refusedDate: string
  acceptedDate: string
  inprogressDate: string
  lastReceivedDate: string
  remindedDate: string
  blockedDate: string
  disappearedDate: string
  advertiserID: string
  brand?: {
    ID: string
    label: string
  }
  tags?: ActionTag[]
}

interface ActionDetailsBrandDTO {
  ID: string
  label: string
  name: string
  createShoppingNotification: boolean
  type: BrandType
  configurations: unknown[] // BrandConfigurationDTO[]
  companyIDs: string[]
}

export interface ActionDetailsDTO {
  ID: string
  status: ActionStatus
  createdDate: string
  updatedDate: string
  domain: string
  lastView: string
  engine: ActionEngine
  country: string
  languages?: string[]
  countries?: string[]
  presence?: string[]
  metas?: Record<string, unknown>
  remindedCount: number
  totalRemindedCount: number
  receivedCount: number
  sendedCount: number
  unreadCount: number
  requestedDate?: string
  ignoredDate?: string
  disappearedDate?: string
  reappearanceDate?: string
  acceptedDate?: string
  refusedDate?: string
  inprogressDate?: string
  lastReceivedDate?: string
  lastStatusDate?: string
  remindedDate?: string
  blockedDate?: string
  brandId: string
  advertiserID: string
  dealer?: Record<string, unknown>
  priority: number
  waiting?: boolean
  history: ActionHistoryDTO[]
  brand: ActionDetailsBrandDTO
  links: ActionLinksDTO
}
